import React from 'react'
import PropTypes from 'prop-types'
import './ComplianceGauge.scss'

/**
 * ComplianceGauge - Visual compliance score gauge
 *
 * @param {number} score - Compliance score (0-100)
 * @param {string} title - Gauge title
 * @param {string} framework - Framework name
 * @param {Object} breakdown - Score breakdown by category
 * @param {boolean} showBreakdown - Show breakdown section
 * @param {boolean} loading - Show loading state
 */
const ComplianceGauge = ({
	score = 0,
	title = 'Compliance Score',
	framework,
	breakdown,
	showBreakdown = true,
	loading = false
}) => {
	const normalizedScore = Math.min(100, Math.max(0, score))

	const getScoreLevel = () => {
		if (normalizedScore >= 90) return { level: 'excellent', label: 'Excellent' }
		if (normalizedScore >= 75) return { level: 'good', label: 'Good' }
		if (normalizedScore >= 50) return { level: 'fair', label: 'Fair' }
		if (normalizedScore >= 25) return { level: 'poor', label: 'Needs Work' }
		return { level: 'critical', label: 'Critical' }
	}

	const getGaugeRotation = () => {
		// Map 0-100 score to -90 to 90 degrees (180 degree arc)
		return -90 + (normalizedScore / 100) * 180
	}

	const { level, label } = getScoreLevel()

	if (loading) {
		return (
			<div className="compliance-gauge">
				<div className="compliance-gauge__header">
					<h3 className="compliance-gauge__title">{title}</h3>
				</div>
				<div className="compliance-gauge__skeleton">
					<div className="skeleton skeleton--gauge"></div>
					<div className="skeleton skeleton--score"></div>
					<div className="skeleton skeleton--breakdown"></div>
				</div>
			</div>
		)
	}

	return (
		<div className={`compliance-gauge compliance-gauge--${level}`}>
			<div className="compliance-gauge__header">
				<h3 className="compliance-gauge__title">{title}</h3>
				{framework && <span className="compliance-gauge__framework">{framework}</span>}
			</div>

			<div className="compliance-gauge__visual">
				<div className="compliance-gauge__meter">
					<svg viewBox="0 0 200 120" className="compliance-gauge__svg">
						{/* Background arc */}
						<path
							d="M 20 100 A 80 80 0 0 1 180 100"
							fill="none"
							stroke="var(--color-gray-200)"
							strokeWidth="12"
							strokeLinecap="round"
						/>
						{/* Colored arc based on score */}
						<path
							d="M 20 100 A 80 80 0 0 1 180 100"
							fill="none"
							className="compliance-gauge__arc"
							strokeWidth="12"
							strokeLinecap="round"
							strokeDasharray={`${(normalizedScore / 100) * 251.2} 251.2`}
						/>
						{/* Needle */}
						<g transform={`rotate(${getGaugeRotation()}, 100, 100)`}>
							<line
								x1="100"
								y1="100"
								x2="100"
								y2="35"
								stroke="var(--color-gray-700)"
								strokeWidth="3"
								strokeLinecap="round"
							/>
							<circle cx="100" cy="100" r="8" fill="var(--color-gray-700)" />
						</g>
					</svg>
				</div>

				<div className="compliance-gauge__score-display">
					<span className="compliance-gauge__score-value">{normalizedScore}</span>
					<span className="compliance-gauge__score-suffix">%</span>
				</div>
				<span className="compliance-gauge__level">{label}</span>
			</div>

			{showBreakdown && breakdown && Object.keys(breakdown).length > 0 && (
				<div className="compliance-gauge__breakdown">
					{Object.entries(breakdown).map(([category, value]) => (
						<div key={category} className="compliance-gauge__breakdown-item">
							<span className="compliance-gauge__breakdown-label">{category}</span>
							<div className="compliance-gauge__breakdown-bar">
								<div
									className="compliance-gauge__breakdown-fill"
									style={{ width: `${value}%` }}
								/>
							</div>
							<span className="compliance-gauge__breakdown-value">{value}%</span>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

ComplianceGauge.propTypes = {
	score: PropTypes.number,
	title: PropTypes.string,
	framework: PropTypes.string,
	breakdown: PropTypes.objectOf(PropTypes.number),
	showBreakdown: PropTypes.bool,
	loading: PropTypes.bool
}

export default ComplianceGauge
